import React, { lazy, Suspense, useContext, useState, useEffect } from 'react';

import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter as Router, BrowserRouterProps, Redirect, Route, Switch } from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';

import Analytics from '@/Analytics';
import BugsnagWrapper from '@/BugsnagWrapper';
import FlatProvider, { Provider } from '@/components/FlatProvider';
import LoadingSpinner from '@/components/LoadingSpinner';
import SessionNotification from '@/components/SessionNotification';
import { WindowDimensionsProvider } from '@/components/WindowDimensionsContext';
import { AuthContext } from '@/core/auth/AuthContext';
import { LoginModal } from '@/core/auth/LoginModal';
import { PrivateRoute } from '@/core/auth/PrivateRoute';
import UsersRoutes from '@/core/users/UsersRoutes';
import { useCheckSessionHeartbeat } from '@/hooks/useCheckSessionHeartbeat';
import { MaterialThemeProvider } from '@/MaterialTheme';
import routes, { baseRoute } from '@/routes';
import SnackbarProvider from '@/SnackbarProvider';
import { getUTCDateDiffInSeconds } from '@/utils/helpers';

function App() {
  return (
    <>
      <FlatProvider providers={[
        WindowDimensionsProvider,
        SnackbarProvider,
        HelmetProvider,
        [QueryParamProvider, { ReactRouterRoute: Route }],
        [Router, { basename: baseRoute }] as Provider<BrowserRouterProps>,
        MaterialThemeProvider,
      ]}>
        <Routes />
      </FlatProvider>
      <Analytics />
    </>
  );
}

function Routes() {
  const { loading, currentAccount, currentSubdomain, currentUser } = useContext(AuthContext);
  const [ showNotification, setShowNotification ] = useState(false);
  const [ showLoginModal, setShowLoginModal ] = useState(false);
  const fiveMinutes = 5 * 60;

  // polls to check if user is still logged in
  const { sessionExpiresAt } = useCheckSessionHeartbeat();

  useEffect(() => {
    if (sessionExpiresAt) {
      const timeRemainingInSeconds = getUTCDateDiffInSeconds(new Date(), new Date(sessionExpiresAt));
      if (timeRemainingInSeconds <= fiveMinutes) {
        setShowNotification(true);
      }
    }
  }, [sessionExpiresAt]);

  const handleOnSignIn = () => {
    setShowNotification(false);
    setShowLoginModal(false);
  };

  if (loading) {
    return (
      <LoadingSpinner />
    );
  }

  if (currentSubdomain?.deactivatedDiscovery && currentSubdomain?.deactivatedValidation) {
    // If account is deactivated, go to another app to display deactivated page
    // If account is uvk-only, redirect there
    // Otherwise, redirect to site2
    if (currentAccount?.uvkAccess && !currentAccount?.coreAccess) {
      window.location.href = '/roadmap/admin';
    } else {
      window.location.href = '/';
    }
    return <LoadingSpinner />;
  }

  return (
    <>
      <BugsnagWrapper>
        <Suspense fallback={<LoadingSpinner />}>
          <div className={showNotification ? 'filter saturate-0' : ''}>
            <Switch>
              <Route path={routes.login} component={lazy(() => import('@/core/auth/AdminLogin'))} />

              <PrivateRoute path={routes.users.self} component={UsersRoutes} accessDeniedPath={routes.login} />

              <Redirect to={routes.users.self} />
            </Switch>
          </div>
          <SessionNotification
            open={showNotification && !!currentUser}
            expiresAt={sessionExpiresAt}
            onClose={() => setShowNotification(false)}
            onRefreshSession={() => setShowLoginModal(true)}
          />
          <LoginModal open={showLoginModal} onClose={() => setShowLoginModal(false)} onSignIn={handleOnSignIn} />
        </Suspense>
      </BugsnagWrapper>
    </>
  );
}

export default App;
